import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { X, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../lib/hooks/useAdmin';
import { Logo } from '../Logo';
import { socialItems, discoverItems, learnItems, toolsItems } from '../../lib/constants/navigationItems';
import { Avatar } from '../Profile/Avatar';
import { supabase } from '../../lib/supabase';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { useAuthProfile } from '../../contexts/AuthProfileContext';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileNav({ isOpen, onClose }: MobileNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthProfile();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    onClose();
    navigate('/');
  };

  const renderNavItem = (item: typeof socialItems[0], index: number) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const delay = `delay-[${index * 50}ms]`;
    
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onClose}
        className={`flex items-center space-x-3 px-6 py-4 rounded-xl transition-all duration-300 ${
          isActive 
            ? 'bg-[#8DA847] text-white' 
            : 'hover:bg-[#8DA847]/10'
        } animate-fade-in ${delay}`}
      >
        <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-[#8DA847]'}`} />
        <span className={`text-lg font-normal ${isActive ? 'text-white' : 'text-[#202020]'}`}>
          {t(item.label)}
        </span>
      </Link>
    );
  };

  return (
    <div
      className={`fixed inset-0 z-[60] transition-opacity duration-300 ease-in-out ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black transition-opacity duration-300 ease-in-out ${
          isOpen ? 'opacity-50' : 'opacity-0'
        }`}
      />
      
      {/* Menu Panel */}
      <div 
        className={`absolute left-0 top-0 h-full w-full max-w-sm bg-white flex flex-col shadow-2xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#8DA847]/10">
          <Link 
            to="/" 
            className="flex items-center space-x-2"
            onClick={onClose}
          >
            <div className="p-2 bg-[#8DA847]/10 rounded-xl">
              <Logo className="h-12 w-12 text-[#8DA847]" />
            </div>
            <span className="text-xl font-gelica font-bold" style={{ color: '#2F4F3E' }}>
              Mindful Family
            </span>
          </Link>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#8DA847]/10 rounded-xl transition-colors"
          >
            <X className="h-6 w-6 text-[#8DA847]" />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 px-4 py-6 space-y-8 overflow-y-auto">
          {/* Social */}
          <div className="space-y-2">
            {socialItems.map((item, index) => renderNavItem(item, index))}
          </div>

          {/* Discover */}
          <div>
            <h3 className="px-6 text-sm font-medium text-[#8DA847] uppercase tracking-wider mb-2">
              {t('nav.discover')}
            </h3>
            <div className="space-y-2">
              {discoverItems.map((item, index) => renderNavItem(item, index + socialItems.length))}
            </div>
          </div>

          {/* Learn */}
          <div>
            <h3 className="px-6 text-sm font-medium text-[#8DA847] uppercase tracking-wider mb-2">
              {t('nav.learn')}
            </h3>
            <div className="space-y-2">
              {learnItems.map((item, index) => renderNavItem(item, index + socialItems.length + discoverItems.length))}
            </div>
          </div>

          {/* Tools - only show if there are items */}
          {toolsItems.length > 0 && (
            <div>
              <h3 className="px-6 text-sm font-medium text-[#8DA847] uppercase tracking-wider mb-2">
                {t('nav.tools')}
              </h3>
              <div className="space-y-2">
                {toolsItems.map((item, index) => renderNavItem(item, index + socialItems.length + discoverItems.length + learnItems.length))}
              </div>
            </div>
          )}

          {/* Admin */}
          {isAdmin && (
            <div>
              <h3 className="px-6 text-sm font-medium text-[#8DA847] uppercase tracking-wider mb-2">
                {t('nav.admin')}
              </h3>
              <Link
                to="/admin"
                onClick={onClose}
                className="flex items-center space-x-3 px-6 py-4 rounded-xl hover:bg-[#8DA847]/10 animate-fade-in"
              >
                <span className="h-5 w-5">⚙️</span>
                <span className="text-lg text-content">Dashboard</span>
              </Link>
            </div>
          )}

          {/* Footer Links - inside navigation, left aligned */}
          <div className="pt-4">
            <div className="px-4 py-3 bg-gray-50 rounded-xl">
              <div className="text-left">
                <p className="text-xs text-gray-500 mb-2">&copy; {new Date().getFullYear()} Mindful Family</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                  <Link to="/privacy-policy" onClick={onClose} className="text-gray-600 hover:text-[#8DA847]">
                    Privacy
                  </Link>
                  <Link to="/terms" onClick={onClose} className="text-gray-600 hover:text-[#8DA847]">
                    Terms
                  </Link>
                  <Link to="/about" onClick={onClose} className="text-gray-600 hover:text-[#8DA847]">
                    About
                  </Link>
                  <Link to="/pricing" onClick={onClose} className="text-gray-600 hover:text-[#8DA847]">
                    Pricing
                  </Link>
                  <Link to="/advertise" onClick={onClose} className="text-gray-600 hover:text-[#8DA847]">
                    Advertise
                  </Link>
                </div>
              </div>
            </div>
            <div className="mt-3 ml-4">
              <LanguageSwitcher />
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="mt-auto p-4 border-t border-[#8DA847]/10 bg-white animate-fade-in">
          {isAuthenticated ? (
            <button
              onClick={handleLogout}
              className="flex items-center justify-center w-full px-6 py-3 space-x-2 bg-[#8DA847]/10 rounded-xl text-[#8DA847] hover:bg-[#8DA847]/20 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>Sign Out</span>
            </button>
          ) : (
            <button
              onClick={() => {
                sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
                window.dispatchEvent(new CustomEvent('show-auth'));
                onClose();
              }}
              className="flex items-center justify-center w-full px-6 py-3 space-x-2 bg-[#8DA847] text-white rounded-xl hover:bg-[#8DA847]/90 transition-colors"
            >
              <span>Sign In</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}